import {useCallback, useEffect, useRef} from 'react';

/**
 * Manage intersection observation for `n` items
 * @param cb Callback when entries are updated
 * @param opts Options
 */
export const useIntersectionObserver = (
	cb: (entries: IntersectionObserverEntry[]) => void,
	opts?: Omit<IntersectionObserverInit, 'root'>,
) => {
	const ref = useRef<HTMLDivElement | null>(null);
	const refs = useRef<
		{
			index: number;
			element: HTMLDivElement;
		}[]
	>([]);

	const observer = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		observer.current = new IntersectionObserver(cb, {
			threshold: 0.51,
			...opts,
			root: ref.current,
		});
		return () => {
			refs.current.forEach((ref) => {
				observer.current?.unobserve(ref.element);
			});
			refs.current = [];
			observer.current?.disconnect();
		};
	}, []);

	/**
	 * Register child ref
	 */
	const registerRef = useCallback((index: number, node: any) => {
		const currentObserver = observer.current;
		const exists = refs.current.find((ref) => ref.index === index);

		// If the node is `null` then remove it
		if (!node) {
			if (exists) {
				currentObserver?.unobserve(exists.element);
				refs.current = refs.current.filter((ref) => ref.index !== index);
			}
			return;
		}

		// Just needs to be updated
		if (exists) {
			exists.element = node;
			currentObserver?.unobserve(exists.element);
			currentObserver?.observe(node);
		}
		// Brand new
		else {
			refs.current = refs.current.concat([
				{
					index,
					element: node,
				},
			]);
			currentObserver?.observe(node);
		}
	}, []);

	/**
	 * Un-register child ref
	 */
	const unregisterRef = useCallback((index: number) => {
		const exists = refs.current.find((ref) => ref.index === index);
		if (!exists) return;

		const currentObserver = observer.current;
		currentObserver?.unobserve(exists.element);
		refs.current = refs.current.filter((ref) => ref.index !== index);
	}, []);

	return {
		registerRef,
		unregisterRef,
		ref,
	};
};

export default useIntersectionObserver;
